import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface NewZone {
  name: string;
  providerId: string;
  type: string;
}

interface AddZoneModalProps {
  showModal: boolean;
  newZone: NewZone;
  providers: any[];
  onClose: () => void;
  onSubmit: () => void;
  onInputChange: (updates: Partial<NewZone>) => void;
}

const AddZoneModal: React.FC<AddZoneModalProps> = ({
  showModal,
  newZone,
  providers,
  onClose,
  onSubmit,
  onInputChange,
}) => {
  return (
    <AnimatePresence>
      {showModal && (
        <motion.div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="card w-full max-w-md mx-4"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
          >
            <div className="card-header">
              <h2 className="text-heading-1 text-slate-900 dark:text-white">Add DNS Zone</h2>
              <p className="text-body-small text-slate-500 dark:text-slate-400 mt-1">
                Create a new DNS zone for domain management
              </p>
            </div>

            <div className="card-body space-y-6">
              <div>
                <label className="form-label">Zone Name</label>
                <input
                  type="text"
                  value={newZone.name}
                  onChange={(e) => onInputChange({ name: e.target.value })}
                  className="form-input"
                  placeholder="example.com"
                />
              </div>

              <div>
                <label className="form-label">DNS Provider</label>
                <select
                  value={newZone.providerId}
                  onChange={(e) => onInputChange({ providerId: e.target.value })}
                  className="form-input"
                >
                  <option value="">Select a provider</option>
                  {providers.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Zone Type</label>
                <select
                  value={newZone.type}
                  onChange={(e) => onInputChange({ type: e.target.value })}
                  className="form-input"
                >
                  <option value="primary">Primary</option>
                  <option value="secondary">Secondary</option>
                </select>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700">
              <div className="flex space-x-3">
                <motion.button
                  onClick={onClose}
                  className="inline-flex items-center justify-center px-4 py-2 text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 font-medium rounded-lg transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex-1"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  onClick={onSubmit}
                  disabled={!newZone.name.trim() || !newZone.providerId}
                  className="inline-flex items-center justify-center px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium rounded-lg transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex-1"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Add Zone
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddZoneModal;
